import { Environment } from './environment.types';

type CommonKeys =
  | 'i18nPrefix'
  | 'defaultImageMagic'
  | 'defaultDebounceTime'
  | 'draftAutosaveMs'
  | 'autoAllocationForcedRoles'
  | 'apiKey'
  | 'cacheControl'
  | 'homePage'
  | 'appVersion'
  | 'productStructure'
  | 'selectsAvailable'
  | 'menu'
  | 'timeToDefaultDate'
  | 'useStatsWebsocket';

export const commonEnvironment: Pick<Environment, CommonKeys> = {
  i18nPrefix: '',
  defaultImageMagic: '/assets/img/default-card-image-magic.jpg',
  defaultDebounceTime: 50,
  draftAutosaveMs: 300000, // 5 min default; per-env override allowed
  autoAllocationForcedRoles: [],
  apiKey: 'solo_test_proprio_test',
  cacheControl: 'no-cache',
  homePage: '/app/shop',
  // Bumped 15.0.0 -> 15.0.1 to one-time-flush the persisted NgRx blob keyed
  // `${appVersion}_stat` (store.metareducers.ts). Older builds could leave a
  // cross-route-polluted list slot (e.g. a stock `product.*` filter in the
  // `orders` slot) frozen in localStorage; it rehydrates on boot and 500s the
  // endpoint. Bumping abandons the old key once; the merged FilterChange guard
  // stops new pollution, and persistence otherwise behaves as before.
  // NOTE: the legacy `_auth` purge is pinned to the historical version
  // (legacy-storage-purge.ts) so this bump does not abandon that cleanup.
  // Bumped again 15.0.1 -> 15.0.2 for the same reason, this time for a leaked
  // SORT: an orders `priority` sort frozen in the `cart` slot kept 500ing
  // /v2/stocks on every visit (FilterChange never rewrites `sort`, so nothing
  // the user could do in the UI cleared it). The PaginationSortChange owner
  // guard stops new pollution; this abandons what is already stored.
  appVersion: 'galactus_ui.15.0.2',
  timeToDefaultDate: new Date(),
  useStatsWebsocket: true,

  productStructure: {
    stockFullColumnFields: ['language', 'condition'],
  },

  selectsAvailable: [
    {
      name: 'IN',
      valid: true,
      translate: 'addLoad',
      icon: 'hourglass_bottom',
      route: 'app/warehouse/session/movement/IN',
      tooltip: '',
    },
    {
      name: 'TRANS',
      valid: true,
      translate: 'addTransfer',
      icon: 'published_with_changes',
      route: 'app/warehouse/session/movement/TRANS',
      tooltip: '',
    },
    {
      name: 'OUT',
      valid: true,
      translate: 'addUnload',
      icon: 'downloading',
      route: 'app/warehouse/session/movement/OUT',
      tooltip: '',
    },
    {
      name: 'Set Insert out',
      valid: true,
      translate: 'setInsert',
      icon: 'dynamic_form',
      route: 'app/set-insert-out',
      tooltip: 'shift.control.z',
    },
  ],

  menu: [
    {
      id: 'warehouse',
      name: 'Warehouse',
      translationKey: 'layout.sidenav.menu.warehouse.main',
      route: '/app/warehouse/stock',
      selectable: false,
      children: [
        {
          id: 'warehouse.session',
          name: 'Session',
          translationKey: 'layout.sidenav.menu.warehouse.session',
          roles: ['user', 'supervisor', 'admin'],
          route: '/app/warehouse/session',
          selectable: true,
        },
        {
          id: 'warehouse.stock',
          name: 'Stock',
          translationKey: 'layout.sidenav.menu.warehouse.stock',
          route: '/app/warehouse/stock',
          selectable: true,
        },
        {
          id: 'warehouse.inventoryTemplate',
          name: 'Inventory template',
          translationKey: 'layout.sidenav.menu.warehouse.inventoryTemplate',
          roles: ['user', 'supervisor', 'admin'],
          route: '/app/warehouse/stock_template',
          selectable: true,
        },
        {
          id: 'warehouse.workingMovements',
          name: 'Working Movements',
          translationKey: 'layout.sidenav.menu.warehouse.workingMovements',
          roles: ['user', 'supervisor', 'admin', 'trainee'],
          route: '/app/warehouse/workingMovements',
          selectable: true,
        },
        {
          id: 'warehouse.sessionWrapper',
          name: 'Session Wrapper',
          translationKey: 'layout.sidenav.menu.warehouse.sessionWrapper',
          roles: ['user', 'supervisor', 'admin'],
          route: '/app/warehouse/sessionWrapper',
          selectable: true,
        },
      ],
    },
    {
      id: 'prices',
      name: 'Prices',
      translationKey: 'layout.sidenav.menu.prices.main',
      roles: ['user', 'supervisor', 'admin'],
      route: '/app/prices/templates',
      selectable: false,
      children: [
        {
          id: 'prices.templates',
          name: 'Templates',
          translationKey: 'layout.sidenav.menu.prices.templates',
          route: '/app/prices/templates',
          selectable: true,
        },
        {
          id: 'prices.buylist',
          name: 'BuyList',
          translationKey: 'layout.sidenav.menu.prices.buylist',
          route: '/app/prices/buylist_templates',
          selectable: true,
        },
        {
          id: 'prices.readBuylist',
          name: 'Read Buylist',
          translationKey: 'layout.sidenav.menu.prices.readBuylist',
          route: '/app/prices/read_buy_templates',
          selectable: true,
        },
      ],
    },
    {
      id: 'shop',
      name: 'Shop',
      translationKey: 'layout.sidenav.menu.shop.main',
      route: '/app/shop/dashboard',
      selectable: false,
      children: [
        {
          id: 'shop.complete',
          name: 'Complete',
          translationKey: 'layout.sidenav.menu.shop.complete',
          roles: ['user', 'supervisor', 'admin'],
          route: '/app/shop/complete',
          selectable: true,
        },
        {
          id: 'shop.dashboard',
          name: 'Dashboard',
          translationKey: 'layout.sidenav.menu.shop.dashboard',
          roles: ['user', 'supervisor', 'admin', 'trainee'],
          route: '/app/shop/dashboard',
          selectable: true,
        },
        {
          id: 'shop.reports',
          name: 'Reports',
          translationKey: 'layout.sidenav.menu.shop.reports',
          roles: ['supervisor', 'admin'],
          route: '/app/shop/reports',
          selectable: true,
        },
        {
          // The kiosk fleet, filed under Shop: an iPad kiosk is a shop's own device, and a shop
          // supervisor must reach it without an administrator on site -- so it cannot sit under
          // Admin, whose parent `roles` would hide the branch from a supervisor. The Shop parent
          // declares no `roles` of its own, which is what lets this child gate individually.
          // The provisioning wizard has no entry of its own any more: the fleet screen links to it.
          id: 'shop.kiosk',
          name: 'Kiosk',
          translationKey: 'layout.sidenav.menu.shop.kiosk',
          roles: ['supervisor', 'admin'],
          route: '/app/kiosk/devices',
          selectable: true,
        },
      ],
    },
    {
      id: 'marketplaces',
      name: 'Marketplaces',
      translationKey: 'layout.sidenav.menu.marketplaces.main',
      roles: ['user', 'supervisor', 'admin'],
      route: '/app/marketplaces/orders',
      selectable: false,
      children: [
        {
          id: 'marketplaces.orders',
          name: 'Orders',
          translationKey: 'layout.sidenav.menu.marketplaces.orders',
          route: '/app/marketplaces/orders',
          selectable: true,
        },
        {
          id: 'marketplaces.stock',
          name: 'Market Stock',
          translationKey: 'layout.sidenav.menu.marketplaces.stock',
          route: '/app/marketplaces/stock',
          selectable: true,
        },
        {
          id: 'marketplaces.statusReport',
          name: 'Order status report',
          translationKey: 'layout.sidenav.menu.statusThroughput.main',
          roles: ['supervisor', 'admin'],
          route: '/app/order-status-report',
          selectable: true,
        },
      ],
    },
    {
      // "Intelligence" = the TCG market + competitive-intelligence area (catalog, pricing, and -- as
      // they land -- tournament results / metagame). Two privilege layers via PER-CHILD roles:
      // Printings/Sealed are the read-only catalog (staff, but NOT trainee); the operator tabs
      // require ingest-operator. A trainee matches no child here, so the whole section -- header
      // included -- disappears from their sidenav. Children are gated on their own roles whether or
      // not the parent has any; a parent `roles` array here would only narrow the whole section.
      id: 'intelligence',
      name: 'Intelligence',
      translationKey: 'layout.sidenav.menu.ingest.main',
      route: '/app/ingest/printings',
      selectable: false,
      children: [
        {
          id: 'intelligence.printings',
          name: 'Printings',
          translationKey: 'layout.sidenav.menu.ingest.printings',
          roles: ['user', 'supervisor', 'admin', 'ingest-operator', 'ingest_operator', 'intelligence-viewer'],
          route: '/app/ingest/printings',
          selectable: true,
        },
        {
          id: 'intelligence.sealed',
          name: 'Sealed',
          translationKey: 'layout.sidenav.menu.ingest.sealed',
          roles: ['user', 'supervisor', 'admin', 'ingest-operator', 'ingest_operator', 'intelligence-viewer'],
          route: '/app/ingest/sealed',
          selectable: true,
        },
        {
          // Intelligence > Scan (spec 2026-10-08): the card scanner of the phone app, same roles as
          // the catalogue. Menu entries carry no icon in this app (MenuItem has no icon field).
          id: 'intelligence.scan',
          name: 'Scan',
          translationKey: 'layout.sidenav.menu.ingest.scan',
          roles: ['user', 'supervisor', 'admin', 'ingest-operator', 'ingest_operator', 'intelligence-viewer'],
          route: '/app/ingest/scan',
          selectable: true,
        },
        {
          id: 'intelligence.status',
          name: 'Status',
          translationKey: 'layout.sidenav.menu.ingest.status',
          roles: ['ingest-operator', 'ingest_operator', 'admin'],
          route: '/app/ingest/status',
          selectable: true,
        },
        {
          id: 'intelligence.adapters',
          name: 'Adapters',
          translationKey: 'layout.sidenav.menu.ingest.adapters',
          roles: ['ingest-operator', 'ingest_operator', 'admin'],
          route: '/app/ingest/adapters',
          selectable: true,
        },
        {
          id: 'intelligence.schedules',
          name: 'Schedules',
          translationKey: 'layout.sidenav.menu.ingest.schedules',
          roles: ['ingest-operator', 'ingest_operator', 'admin'],
          route: '/app/ingest/schedules',
          selectable: true,
        },
        {
          id: 'intelligence.runs',
          name: 'Runs',
          translationKey: 'layout.sidenav.menu.ingest.runs',
          roles: ['ingest-operator', 'ingest_operator', 'admin'],
          route: '/app/ingest/runs',
          selectable: true,
        },
        {
          id: 'intelligence.matchQueue',
          name: 'Match queue',
          translationKey: 'layout.sidenav.menu.ingest.matchQueue',
          roles: ['ingest-operator', 'ingest_operator', 'admin'],
          route: '/app/ingest/match-queue',
          selectable: true,
        },
        {
          id: 'intelligence.failedPayloads',
          name: 'Failed payloads',
          translationKey: 'layout.sidenav.menu.ingest.failedPayloads',
          roles: ['ingest-operator', 'ingest_operator', 'admin'],
          route: '/app/ingest/failed-payloads',
          selectable: true,
        },
      ],
    },
    {
      id: 'admin',
      name: 'Admin',
      translationKey: 'layout.sidenav.menu.admin.main',
      roles: ['admin'],
      route: '/app/admin/users',
      selectable: false,
      children: [
        {
          id: 'admin.userManagement',
          name: 'User Management',
          translationKey: 'layout.sidenav.menu.admin.userManagement',
          roles: ['admin'],
          route: '/app/admin/users',
          selectable: true,
        },
      ],
    },
  ],
};
